'use client'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { IconCircleCheck } from '@tabler/icons-react'

import {
  MarketingConsentsCheckboxes,
  MarketingConsentsValues
} from 'modules/customer'
import { signUpToNewsletter } from 'modules/newsletter'
import Button from 'shared/ui/Button'
import Checkbox from 'shared/ui/form/Checkbox/Checkbox'
import {
  ControlledCheckbox
} from 'shared/ui/form/ControlledElements/ControlledCheckbox'
import PasswordField from 'shared/ui/form/PasswordField'
import TextField from 'shared/ui/form/TextField'
import Link from 'shared/ui/Link/Link'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { PATHS } from 'shared/utils/paths'

import { gtmSignupEvent } from '../analytics'
import { getVerificationSms, registerUser } from '../api/auth'
import { registrationSchema } from '../validation/registerSchema'

import ModalSmsConfirmation from './ModalSmsConfirmation'

import styles from './RegisterForm.module.scss'

const defaultValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  regulationsAccepted: false,
  allAgreementsConsent: false,
  smsConsentsGranted: false,
  isNewsletterSubscriber: false,
  verificationSmsCode: ''
}

const RegisterForm = () => {
  const [emailSent, setEmailSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const methods = useForm({
    resolver: yupResolver(registrationSchema),
    defaultValues,
    mode: 'onSubmit'
  })
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
    getValues
  } = methods

  const phoneNumber = watch('phoneNumber')
  const email = watch('email')

  const isNewsletterSubscriber = watch('isNewsletterSubscriber')
  const smsConsentsGranted = watch('smsConsentsGranted')
  const allAgreementsConsent = watch('allAgreementsConsent')
  const regulationsAccepted = watch('regulationsAccepted')

  useEffect(() => {
    //select/unselect allAgreementsConsent depends on agreements changing

    const agreements = [
      isNewsletterSubscriber,
      smsConsentsGranted,
      regulationsAccepted
    ]

    if (allAgreementsConsent) {
      if (agreements.some(agreement => !agreement)) {
        setValue('allAgreementsConsent', false)
      }
    } else {
      if (agreements.every(agreement => agreement)) {
        setValue('allAgreementsConsent', true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewsletterSubscriber, smsConsentsGranted, regulationsAccepted])
  const onSubmit = (verificationSmsCode: string) => {
    setIsLoading(true)
    closeModal()
    const data = getValues()

    const {
      isNewsletterSubscriber: isNewsletterSubscriberValue,
      ...valuesPayload
    } = data
    const emailLowerCase = data.email.toLowerCase()

    const payload = {
      ...valuesPayload,
      verificationSmsCode,
      email: emailLowerCase
    }

    registerUser(payload)
      .then(async resp => {
        const userData = resp.data.data

        if (isNewsletterSubscriberValue) {
          await signUpToNewsletter({
            email: emailLowerCase,
            formType: 'registration'
          }).catch(() => {})
        }

        return userData
      })
      .then(userData => {
        gtmSignupEvent(userData)
        setModalOpen(false)
        setEmailSent(true)
        setIsLoading(false)
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        setIsLoading(false)
      })
  }

  const userDataFormSubmit = async () => {
    setIsLoading(true)
    try {
      await getVerificationSms(phoneNumber)
      setModalOpen(true)
    } catch (err) {
      toast.error(getApiErrors(err))
    } finally {
      setIsLoading(false)
    }
  }

  const checkAdditionalAgreements = (values: MarketingConsentsValues) => {
    if ('smsConsentsGranted' in values)
      setValue('smsConsentsGranted', values.smsConsentsGranted)
    if ('isNewsletterSubscriber' in values)
      setValue('isNewsletterSubscriber', values.isNewsletterSubscriber)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  return emailSent ? (
    <div>
      <div className={styles.emailSentHeader} autoFocus>
        <IconCircleCheck className={styles.checkIcon} />
        Link aktywacyjny został wysłany
      </div>
      <div className={styles.emailSentDescription}>
        Na maila: <span>{email}</span> został wysłany link aktywacyjny. <br />
        Kliknij aby aktywować swoje konto i w pełni korzystać z naszego serwisu.
      </div>
      <Link href={PATHS.login}>
        <Button id='register_form_login_redirect' variant='primary' fullWidth>
          Przejdź do logowania
        </Button>
      </Link>
    </div>
  ) : (
    <FormProvider {...methods}>
      <form
        noValidate
        className={styles.formWrapper}
        onSubmit={handleSubmit(userDataFormSubmit)}
      >
        <div className={styles.formHeader}>Załóż konto</div>
        <div className={styles.row}>
          <TextField
            id='register_form_first_name'
            required
            label='Imię'
            inputProps={{ autoComplete: 'given-name' }}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName?.message}
            {...register('firstName')}
          />
          <TextField
            id='register_form_last_name'
            required
            label='Nazwisko'
            inputProps={{ autoComplete: 'family-name' }}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>
        <TextField
          id='register_form_phone'
          required
          label='Numer telefonu'
          inputProps={{ autoComplete: 'tel-national' }}
          error={Boolean(errors.phoneNumber)}
          helperText={errors.phoneNumber?.message}
          icons={{
            start: <div>+48</div>
          }}
          {...register('phoneNumber')}
        />
        <TextField
          id='register_form_email'
          required
          label='Adres email'
          inputProps={{ autoComplete: 'email' }}
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          {...register('email')}
        />
        <PasswordField
          required
          id='register_form_password'
          label='Hasło'
          inputProps={{ autoComplete: 'new-password' }}
          withPasswordHint
          valueToPasswordHint={watch('password') || ''}
          error={Boolean(errors.password)}
          helperText={errors.password?.message}
          {...register('password')}
        />
        <PasswordField
          id='register_form_password_confirmation'
          required
          label='Powtórz hasło'
          inputProps={{ autoComplete: 'new-password' }}
          error={Boolean(errors.passwordConfirmation)}
          helperText={errors.passwordConfirmation?.message}
          {...register('passwordConfirmation')}
        />
        <Controller
          control={methods.control}
          name='allAgreementsConsent'
          render={({ field }) => {
            const handleChange = (_: string, value: boolean) => {
              field.onChange(value)
              setValue('regulationsAccepted', value)
              setValue('smsConsentsGranted', value)
              setValue('isNewsletterSubscriber', value)
            }

            return (
              <Checkbox
                name='allAgreementsConsent'
                label='Zaznacz wszystkie zgody'
                onChange={handleChange}
                value={field.value}
              />
            )
          }}
        />

        <ControlledCheckbox
          id='register_form_regulations_consent'
          name='regulationsAccepted'
          label={
            <div className={styles.customLabel}>
              Akceptuję
              <Link
                href={PATHS.termsAndConditions}
                rel='noreferrer'
                target='_blank'
              >
                regulamin lokalnyrolnik.pl
              </Link>
            </div>
          }
          required
        />
        <MarketingConsentsCheckboxes
          values={{
            isNewsletterSubscriber: isNewsletterSubscriber,
            smsConsentsGranted: smsConsentsGranted
          }}
          onChange={checkAdditionalAgreements}
        />

        <div className={styles.buttonsWrapper}>
          <Button
            id='register_form_create_account_button'
            fullWidth
            variant='primary'
            size='large'
            type='submit'
            disabled={isLoading}
            aria-label='Załóż konto'
            aria-disabled={isLoading}
            aria-describedby={
              isLoading && errors ? 'disabled-reason' : undefined
            }
          >
            Załóż konto
          </Button>
          {modalOpen && (
            <ModalSmsConfirmation
              onClose={closeModal}
              onConfirm={onSubmit}
              phoneNumber={phoneNumber || ''}
            />
          )}
          <div className={styles.hasAccount}>
            Masz już konto? <Link href={PATHS.login}>Zaloguj się</Link>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default RegisterForm
